import { useNavigate } from 'react-router-dom';
import { Zap, Radio, Activity, Plus, RefreshCw, Users } from 'lucide-react';
import { useStrategies, useAdminIdentityUsers, useSystemHealth, useSubscriptions } from '../../api/hooks';
import { StatCard, SectionHeader, EmptyState, ErrorState } from '../../components/ui';
import { fmtRelative, symbolShort } from '../../lib/utils';

function TableRowSkeleton() {
  return (
    <tr className="border-b border-bg-border/50 animate-pulse">
      <td className="py-3 px-4">
        <div className="h-3.5 bg-bg-border/60 rounded w-36 mb-1.5" />
        <div className="h-2.5 bg-bg-border/40 rounded w-16" />
      </td>
      <td className="py-3 px-4"><div className="h-3 bg-bg-border/40 rounded w-20" /></td>
      <td className="py-3 px-4"><div className="h-3 bg-bg-border/40 rounded w-12" /></td>
      <td className="py-3 px-4"><div className="h-3 bg-bg-border/40 rounded w-14" /></td>
      <td className="py-3 px-4"><div className="h-3 bg-bg-border/40 rounded w-24" /></td>
    </tr>
  );
}

function StrategyRow({ strategy, onClick }) {
  return (
    <tr
      onClick={onClick}
      className="border-b border-bg-border/50 hover:bg-bg-hover cursor-pointer transition-colors group"
    >
      <td className="py-3 px-4 font-semibold text-txt-primary group-hover:text-amber transition-colors text-xs">
        {strategy.name}
        <div className="text-2xs text-txt-muted font-mono mt-0.5">ID:{strategy.id}</div>
      </td>
      <td className="py-3 px-4 font-mono text-xs text-txt-secondary">{symbolShort(strategy.symbol)}</td>
      <td className="py-3 px-4 font-mono text-xs text-txt-secondary">{strategy.timeframe}</td>
      <td className="py-3 px-4 font-mono text-xs text-txt-secondary">{strategy.rules?.length ?? 0} rules</td>
      <td className="py-3 px-4 font-mono text-xs text-txt-muted">{fmtRelative(strategy.created_at)}</td>
    </tr>
  );
}

function StrategyCardSkeleton() {
  return (
    <div className="rounded-lg border border-bg-border bg-bg-surface/40 p-3 mb-2.5 animate-pulse">
      <div className="h-3.5 bg-bg-border/60 rounded w-2/3 mb-2" />
      <div className="h-2.5 bg-bg-border/40 rounded w-1/3" />
    </div>
  );
}

function StrategyCard({ strategy, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-lg border border-bg-border bg-bg-surface/40 p-3 mb-2.5 hover:bg-bg-hover transition-colors"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-semibold text-txt-primary truncate">{strategy.name}</span>
        <span className="text-2xs font-mono text-txt-muted shrink-0">ID:{strategy.id}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2xs font-mono text-txt-muted">
        <span>{symbolShort(strategy.symbol)}</span>
        <span>{strategy.timeframe}</span>
        <span>{strategy.rules?.length ?? 0} rules</span>
        <span>{fmtRelative(strategy.created_at)}</span>
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data: strategies, isLoading, error, refetch } = useStrategies();
  const { data: users }  = useAdminIdentityUsers();
  const { data: health } = useSystemHealth();
  const { data: subs }   = useSubscriptions();

  const allStrategies = strategies || [];
  const symbols = subs?.symbols || subs || [];

  return (
    <div className="space-y-6 max-w-7xl">

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Strategies"
          value={allStrategies.length}
          icon={Zap}
          sub="Configured"
        />
        <StatCard
          label="Users"
          value={users?.length ?? '—'}
          icon={Users}
          sub="Registered"
        />
        <StatCard
          label="Live Feed"
          value={health?.websocket ? 'CONNECTED' : 'OFFLINE'}
          icon={Radio}
          accent={health?.websocket ? 'text-bull' : 'text-bear'}
          sub="FYERS WebSocket"
        />
        <StatCard
          label="Subscriptions"
          value={Array.isArray(symbols) ? symbols.length : '—'}
          icon={Activity}
          sub="Active symbols"
        />
      </div>

      {/* Live feed status */}
      <div className="panel p-4">
        <SectionHeader title="System Status" subtitle="Infrastructure health" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'WebSocket',    ok: health?.websocket },
            { label: 'Redis',        ok: health?.redis },
            { label: 'Candle Feed',  ok: health?.candle_feed ?? health?.websocket },
            { label: 'Strategy API', ok: !error },
          ].map(({ label, ok }) => (
            <div key={label} className="bg-bg-surface rounded-lg p-3 flex items-center gap-3">
              <span className={`w-2 h-2 rounded-full shrink-0 ${
                ok === true  ? 'bg-bull animate-pulse' :
                ok === false ? 'bg-bear animate-pulse' : 'bg-txt-muted'
              }`} />
              <div>
                <div className="text-xs font-medium text-txt-primary">{label}</div>
                <div className={`text-2xs font-mono ${
                  ok === true ? 'text-bull' : ok === false ? 'text-bear' : 'text-txt-muted'
                }`}>
                  {ok === true ? 'HEALTHY' : ok === false ? 'DOWN' : 'UNKNOWN'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active symbol subscriptions */}
      {Array.isArray(symbols) && symbols.length > 0 && (
        <div className="panel p-4">
          <SectionHeader title="Active Subscriptions" subtitle="Symbols receiving live feed" />
          <div className="flex flex-wrap gap-2">
            {symbols.map((sym) => (
              <span key={sym} className="badge bg-bull/10 border border-bull/30 text-bull">
                {symbolShort(sym)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Strategies table */}
      <div className="panel p-4">
        <SectionHeader
          title="Strategies"
          subtitle={`${allStrategies.length} configured`}
          actions={
            <div className="flex items-center gap-2">
              <button onClick={refetch} className="btn-ghost flex items-center gap-1.5 text-xs">
                <RefreshCw size={12} /> Refresh
              </button>
              <button onClick={() => navigate('/strategies/create')} className="btn-primary flex items-center gap-1.5">
                <Plus size={13} /> New Strategy
              </button>
            </div>
          }
        />

        {error ? (
          <ErrorState message={error.message} onRetry={refetch} />
        ) : isLoading ? (
          <>
            <div className="hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-bg-border">
                      {['Strategy', 'Symbol', 'Timeframe', 'Rules', 'Created'].map((h) => (
                        <th key={h} className="text-left py-2 px-4 text-2xs font-mono text-txt-label uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{[0,1,2,3,4].map((i) => <TableRowSkeleton key={i} />)}</tbody>
                </table>
              </div>
            </div>
            <div className="lg:hidden">
              {[0,1,2,3,4].map((i) => <StrategyCardSkeleton key={i} />)}
            </div>
          </>
        ) : allStrategies.length === 0 ? (
          <EmptyState
            title="No strategies yet"
            description="Create your first algorithmic trading strategy"
            action={
              <button onClick={() => navigate('/strategies/create')} className="btn-primary mt-2">
                <Plus size={13} className="inline mr-1" /> Create Strategy
              </button>
            }
          />
        ) : (
          <>
            <div className="hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-bg-border">
                      {['Strategy', 'Symbol', 'Timeframe', 'Rules', 'Created'].map((h) => (
                        <th key={h} className="text-left py-2 px-4 text-2xs font-mono text-txt-label uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allStrategies.map((s) => (
                      <StrategyRow
                        key={s.id}
                        strategy={s}
                        onClick={() => navigate(`/strategies/${s.id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="lg:hidden">
              {allStrategies.map((s) => (
                <StrategyCard
                  key={s.id}
                  strategy={s}
                  onClick={() => navigate(`/strategies/${s.id}`)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Quick actions */}
      <div className="panel p-4">
        <SectionHeader title="Quick Actions" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: 'New Strategy',   action: () => navigate('/strategies/create'),  style: 'btn-primary w-full' },
            { label: 'All Strategies', action: () => navigate('/strategies'),       style: 'btn-outline w-full' },
            { label: 'Alert History',  action: () => navigate('/alerts'),           style: 'btn-outline w-full' },
            { label: 'System Health',  action: () => navigate('/admin/health'),     style: 'btn-outline w-full' },
          ].map(({ label, action, style }) => (
            <button key={label} onClick={action} className={style}>{label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
